// src/components/PublicProfile.jsx
// Public profile page accessible without login — /@nickname
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../context/UserContext";
import { STATUS_LABEL, STATUS_EMOJI, STATUS_COLOR, FRONTEND_URL } from "../constants";
import { useTranslation } from "react-i18next";

function ensureGoogleMaps(cb) {
  if (window.google?.maps?.Map) { cb(); return; }
  if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
    return;
  }
  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=marker&v=weekly&loading=async&language=${localStorage.getItem("lang") || "en"}`;
  script.async = true;
  script.onload = () => {
    if (window.google?.maps?.Map) { cb(); return; }
    const check = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(check); cb(); }
    }, 100);
  };
  document.head.appendChild(script);
}

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54", primaryDim: "#595149",
  bg: "#faf9f6", surfaceLow: "#f4f4f0",
  container: "#edeeea", containerLowest: "#ffffff",
  onSurface: "#2f3430", onSurfaceVariant: "#5c605c",
  outlineVariant: "#8a8e8a", primaryContainer: "#ede0d5",
};

const mobile = () => window.innerWidth <= 768;

export default function PublicProfile({ nickname }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const isMobile = mobile();

  const [curatedLists, setCuratedLists] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/lists/public/${nickname}`)
      .then((res) => setCuratedLists(res.data))
      .catch(() => {});
  }, [nickname]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/users/${nickname}`),
      axios.get(`${API_BASE}/users/${nickname}/public-places`),
    ])
      .then(([profileRes, placesRes]) => {
        setProfile(profileRes.data);
        setPlaces(placesRes.data);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        setError(detail === "This profile is private" ? "This profile is private" : "Profile not found");
      })
      .finally(() => setLoading(false));
  }, [nickname]);

  // Initialize Google Map (ensure SDK is loaded first)
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!places.length || !mapContainerRef.current) return;
    ensureGoogleMaps(() => setMapReady(true));
  }, [places]);

  useEffect(() => {
    if (!mapReady || !places.length || !mapContainerRef.current) return;
    if (mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    places.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: bounds.getCenter(),
      zoom: 13,
      minZoom: 6,
      restriction: { latLngBounds: { north: 85, south: -85, west: -180, east: 180 }, strictBounds: false },
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: "greedy",
      mapId: process.env.REACT_APP_GOOGLE_MAP_ID || "DEMO_MAP_ID",
    });
    map.fitBounds(bounds, { top: 60, bottom: isMobile ? 200 : 60, left: 40, right: 40 });
    mapRef.current = map;

    return () => { mapRef.current = null; };
  }, [mapReady, places, isMobile]);

  // Update markers
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    markersRef.current.forEach((m) => (m.map = null));
    markersRef.current = [];

    const filtered = filter === "all" ? places : places.filter((p) => p.status === filter);
    filtered.forEach((p) => {
      const sc = STATUS_COLOR[p.status];
      const content = document.createElement("div");
      content.innerHTML = `<div style="
        padding:4px 10px;border-radius:20px;
        background:${sc?.bg || C.surfaceLow};color:${sc?.color || C.onSurface};
        font-family:${FL};font-size:11px;font-weight:700;
        white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);
        border:2px solid white;
      ">${STATUS_EMOJI[p.status]} ${p.name}</div>`;
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: p.lat, lng: p.lng },
        map: mapRef.current,
        content,
      });
      markersRef.current.push(marker);
    });
  }, [mapReady, places, filter]);

  const shareProfile = () => {
    const url = `${FRONTEND_URL}/@${encodeURIComponent(nickname)}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, flexDirection: "column", gap: 10 }}>
        <img src="/logo.svg" alt="" style={{ width: 48, height: 48 }} />
        <p style={{ fontFamily: FL, fontSize: 11, color: C.outlineVariant }}>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, flexDirection: "column", gap: 16 }}>
        <img src="/logo.svg" alt="" style={{ width: 48, height: 48 }} />
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.outlineVariant }}>{error}</p>
        <a href="/" style={{ fontFamily: FL, fontSize: 12, color: C.primary, textDecoration: "none", padding: "8px 20px", background: C.primaryContainer, borderRadius: 8, fontWeight: 600 }}>
          {t("curated.start_your_own")}
        </a>
      </div>
    );
  }

  const filtered = filter === "all" ? places : places.filter((p) => p.status === filter);
  const stats = {
    all: places.length,
    want_to_go: places.filter((p) => p.status === "want_to_go").length,
    visited: places.filter((p) => p.status === "visited").length,
    want_revisit: places.filter((p) => p.status === "want_revisit").length,
  };

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: C.bg }}>
      {/* Header */}
      <header style={{
        padding: isMobile ? "14px 16px" : "16px 32px",
        background: "rgba(250,249,246,0.9)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${C.container}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
          <img src="/logo.svg" alt="" style={{ width: isMobile ? 28 : 36, height: isMobile ? 28 : 36 }} />
          <div>
            <h1 style={{ margin: 0, fontFamily: FH, fontStyle: "italic", fontSize: isMobile ? 18 : 22, color: C.primary }}>
              {profile.nickname}
            </h1>
            <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant, letterSpacing: "0.1em" }}>
              {profile.place_count} {t("search.places").toLowerCase()} &middot; {profile.follower_count} {t("profile.followers").toLowerCase()}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, flexShrink: 0 }}>
          {/* Share */}
          <button onClick={shareProfile} style={{
            fontFamily: FL, fontSize: 10, fontWeight: 600,
            color: C.primary, background: C.container, border: "none",
            padding: "6px 10px", borderRadius: 6, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 3,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>share</span>
            {copied ? t("place.copied") : t("place.share")}
          </button>
          {!isMobile && profile.instagram_url && (
            <a href={profile.instagram_url} target="_blank" rel="noreferrer" style={{
              fontFamily: FL, fontSize: 10, color: "#E1306C", textDecoration: "none",
              padding: "6px 12px", background: "#fce4ec", borderRadius: 6, fontWeight: 600,
            }}>Instagram</a>
          )}
          {!isMobile && profile.blog_url && (
            <a href={profile.blog_url} target="_blank" rel="noreferrer" style={{
              fontFamily: FL, fontSize: 10, color: "#03C75A", textDecoration: "none",
              padding: "6px 12px", background: "#e6f9ee", borderRadius: 6, fontWeight: 600,
            }}>Blog</a>
          )}
          <a href="/" style={{
            fontFamily: FL, fontSize: isMobile ? 10 : 11, fontWeight: 700, color: "#fff6ef",
            padding: isMobile ? "6px 12px" : "8px 18px", background: C.primary, borderRadius: 8, textDecoration: "none",
            whiteSpace: "nowrap",
          }}>
            {t("curated.start_your_own")}
          </a>
        </div>
      </header>

      {/* Filter bar */}
      <div style={{
        padding: "10px 16px", display: "flex", gap: 8, overflowX: "auto",
        background: C.bg, borderBottom: `1px solid ${C.container}`,
        zIndex: 10,
      }}>
        {[
          { key: "all", label: t("map.all"), count: stats.all },
          { key: "want_to_go", label: `🔖 ${t("status.want_to_go")}`, count: stats.want_to_go },
          { key: "visited", label: `✅ ${t("status.visited")}`, count: stats.visited },
          { key: "want_revisit", label: `❤️ ${t("status.want_revisit")}`, count: stats.want_revisit },
        ].map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: "6px 14px", borderRadius: 20,
              border: "none", cursor: "pointer", whiteSpace: "nowrap",
              fontFamily: FL, fontSize: 11, fontWeight: 600,
              background: filter === f.key ? C.primary : C.surfaceLow,
              color: filter === f.key ? "#fff6ef" : C.onSurfaceVariant,
              transition: "all 0.15s",
            }}>
            {f.label} {f.count > 0 ? f.count : ""}
          </button>
        ))}
      </div>

      {/* Main: map + list */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Map */}
        <div ref={mapContainerRef} style={{ flex: 1, minHeight: 0 }} />

        {/* Side list (desktop only) */}
        {!isMobile && (
          <div style={{
            width: 360, overflowY: "auto", background: C.bg,
            borderLeft: `1px solid ${C.container}`, padding: "16px 20px",
          }}>
            <p style={{ margin: "0 0 12px", fontFamily: FL, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant }}>
              {profile.nickname} &mdash; {filtered.length} {t("search.places").toLowerCase()}
            </p>
            {/* Curated Lists */}
            {curatedLists.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: "0 0 8px", fontFamily: FL, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: C.primary }}>
                  {t("curated.title")}
                </p>
                {curatedLists.map((cl) => (
                  <a key={cl.id} href={`/list/${cl.id}`} style={{
                    display: "block", padding: "10px 12px", borderBottom: `1px solid ${C.container}`,
                    textDecoration: "none", transition: "background 0.1s",
                  }}>
                    <p style={{ margin: 0, fontFamily: FH, fontSize: 13, fontWeight: 600, color: C.onSurface }}>{cl.title}</p>
                    <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>
                      {cl.item_count} {t("search.places").toLowerCase()} {cl.description ? `· ${cl.description}` : ""}
                    </p>
                  </a>
                ))}
              </div>
            )}

            {filtered.map((p) => (
              <PlaceCard key={p.id} place={p} mapRef={mapRef} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom list sheet */}
      {isMobile && (
        <MobileBottomSheet places={filtered} profile={profile} mapRef={mapRef} curatedLists={curatedLists} />
      )}
    </div>
  );
}

/* Place card — shows photo if available */
function PlaceCard({ place: p, mapRef }) {
  const sc = STATUS_COLOR[p.status];
  return (
    <div
      onClick={() => {
        if (mapRef.current && window.google?.maps) {
          mapRef.current.panTo({ lat: p.lat, lng: p.lng });
          mapRef.current.setZoom(16);
        }
      }}
      style={{
        padding: "12px 0", borderBottom: `1px solid ${C.container}`,
        cursor: "pointer", transition: "background 0.1s",
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div style={{ display: "flex", gap: 10 }}>
        {/* Photo thumbnail */}
        {p.photo_url && (
          <img src={p.photo_url} alt="" style={{
            width: 56, height: 56, borderRadius: 8,
            objectFit: "cover", flexShrink: 0,
          }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{
              fontFamily: FL, fontSize: 9, fontWeight: 600,
              padding: "2px 6px", borderRadius: 4,
              background: sc?.bg || C.surfaceLow, color: sc?.color || C.onSurfaceVariant,
            }}>
              {STATUS_EMOJI[p.status]} {STATUS_LABEL[p.status]}
            </span>
            {p.rating > 0 && (
              <span style={{ fontFamily: FL, fontSize: 9, color: C.primary }}>
                {"★".repeat(p.rating)}
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontFamily: FH, fontSize: 14, color: C.onSurface }}>{p.name}</p>
          {p.address && (
            <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.address}</p>
          )}
          {p.memo && (
            <p style={{ margin: "4px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 11, color: C.onSurfaceVariant, lineHeight: 1.5 }}>
              "{p.memo}"
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileBottomSheet({ places, profile, mapRef, curatedLists }) {
  const { t } = useTranslation();
  const PEEK = 160;
  const FULL_VH = 70;
  const [mode, setMode] = useState("peek"); // "peek" | "full"
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const startH = useRef(0);
  const dragging = useRef(false);

  const getMaxH = () => window.innerHeight * (FULL_VH / 100);

  const onTouchStart = useCallback((e) => {
    dragging.current = true;
    startY.current = e.touches[0].clientY;
    startH.current = sheetRef.current?.offsetHeight || PEEK;
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragging.current || !sheetRef.current) return;
    const dy = startY.current - e.touches[0].clientY;
    const newH = Math.max(PEEK, Math.min(getMaxH(), startH.current + dy));
    sheetRef.current.style.height = newH + "px";
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!dragging.current || !sheetRef.current) return;
    dragging.current = false;
    sheetRef.current.style.transition = "height 0.3s ease";
    const curH = sheetRef.current.offsetHeight;
    const mid = (PEEK + getMaxH()) / 2;
    if (curH > mid) {
      sheetRef.current.style.height = getMaxH() + "px";
      setMode("full");
    } else {
      sheetRef.current.style.height = PEEK + "px";
      setMode("peek");
    }
  }, []);

  const toggleMode = () => {
    const next = mode === "peek" ? "full" : "peek";
    setMode(next);
    if (sheetRef.current) {
      sheetRef.current.style.transition = "height 0.3s ease";
      sheetRef.current.style.height = (next === "full" ? getMaxH() : PEEK) + "px";
    }
  };

  return (
    <div
      ref={sheetRef}
      style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: PEEK,
        background: C.containerLowest,
        borderTopLeftRadius: 16, borderTopRightRadius: 16,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
        transition: "height 0.3s ease",
        display: "flex", flexDirection: "column",
        zIndex: 20,
      }}
    >
      {/* Drag handle */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={toggleMode}
        style={{ padding: "12px 16px", cursor: "pointer", textAlign: "center", flexShrink: 0 }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.container, margin: "0 auto 10px" }} />
        <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 700, color: C.onSurface }}>
          {profile.nickname} &mdash; {places.length} {t("search.places").toLowerCase()}
        </p>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Curated Lists */}
        {curatedLists.length > 0 && (
          <div style={{ padding: "0 16px 8px" }}>
            {curatedLists.map((cl) => (
              <a key={cl.id} href={`/list/${cl.id}`} style={{
                display: "block", padding: "8px 0", borderBottom: `1px solid ${C.container}`,
                textDecoration: "none",
              }}>
                <span style={{ fontFamily: FH, fontSize: 13, color: C.onSurface }}>{cl.title}</span>
                <span style={{ fontFamily: FL, fontSize: 10, color: C.outlineVariant, marginLeft: 6 }}>{cl.item_count} {t("search.places").toLowerCase()}</span>
              </a>
            ))}
          </div>
        )}

        {places.map((p) => {
          const sc = STATUS_COLOR[p.status];
          return (
            <div key={p.id}
              onClick={() => {
                if (mapRef.current && window.google?.maps) {
                  mapRef.current.panTo({ lat: p.lat, lng: p.lng });
                  mapRef.current.setZoom(16);
                }
              }}
              style={{ padding: "10px 16px", borderTop: `1px solid ${C.container}`, cursor: "pointer" }}>
              <div style={{ display: "flex", gap: 10 }}>
                {p.photo_url && (
                  <img src={p.photo_url} alt="" style={{
                    width: 44, height: 44, borderRadius: 6,
                    objectFit: "cover", flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{
                      fontFamily: FL, fontSize: 9, fontWeight: 600,
                      padding: "2px 6px", borderRadius: 4,
                      background: sc?.bg, color: sc?.color,
                    }}>
                      {STATUS_EMOJI[p.status]}
                    </span>
                    <span style={{ fontFamily: FH, fontSize: 13, color: C.onSurface }}>{p.name}</span>
                    {p.rating > 0 && (
                      <span style={{ fontFamily: FL, fontSize: 9, color: C.primary }}>{"★".repeat(p.rating)}</span>
                    )}
                  </div>
                  {p.memo && (
                    <p style={{ margin: "2px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 11, color: C.onSurfaceVariant }}>"{p.memo}"</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
